/**
 * Asking a browser to buzz, and remembering that it said yes.
 *
 * Three separate things have to be true before a push can arrive, and they are
 * owned by three different parties:
 *
 *   1. The BROWSER has granted permission. Only the person can give this, once,
 *      from a prompt we are only allowed to raise in response to a click.
 *   2. The DEVICE has a subscription, which the browser mints and we store.
 *   3. The ACCOUNT has the kind switched on, which lives on the server because
 *      it is a decision about the person, not about this laptop.
 *
 * The switch in settings is (2). It cannot fake (1) — a browser that has been
 * told "never" gives no second prompt, and pretending otherwise would be a
 * toggle that moves and does nothing.
 */
import { api } from "./api.jsx";


/**
 * The VAPID key, as the subscription API wants it.
 *
 * `applicationServerKey` takes bytes, not the base64url string every VAPID tool
 * prints. Padding has to be restored and the URL-safe alphabet translated back
 * before `atob` will touch it — get this wrong and `subscribe()` fails with
 * "InvalidCharacterError", which says nothing about keys at all.
 */
function keyBytes(base64url) {
  const padded = (base64url + '='.repeat((4 - (base64url.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = window.atob(padded);
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

/**
 * THE NATIVE APP, WHERE THERE IS ONE, AND WHAT IT CHANGES.
 *
 * Inside the Android app a web push is delivered by CHROME, which posts it under
 * its own icon and name and opens itself when it is tapped. The owner, after an
 * evening of it: "I think we should scrap web push and do it natively" — and
 * then the line that scoped it: "I meant scrap web push for the native apps
 * versions, not from the actual web page."
 *
 * So the app injects this object before our code runs, and its presence is the
 * whole signal: inside it, notifications are the app's own — our icon, our
 * channel, a tap that opens the app — and the browser's push machinery is not
 * used at all. Everywhere else nothing changes.
 *
 * A METHOD RATHER THAN A VALUE, because the token is not always ready at first
 * paint: Firebase mints it asynchronously, and a field would be empty exactly
 * once — on the first run after installing, which is the run that matters.
 */
function nativeApp() {
  return (typeof window !== "undefined" && window.CardboardmarketAndroid) || null;
}

/**
 * WHETHER THE SERVER KNOWS ABOUT THIS INSTALLATION, which is not the same
 * question as whether the app holds a token.
 *
 * REPORTED: "The setting appears enabled and I can't disable it." The switch was
 * reading `getToken()` — so it said ON because Firebase had issued a token, and
 * turning it off deleted the row and then read the token again, which was still
 * there. A switch that springs back is a switch that is describing the wrong
 * thing.
 *
 * A token is a fact about the INSTALLATION and it does not go away when somebody
 * asks not to be notified; what goes away is our permission to use it. So that
 * is what is remembered here, keyed by the token itself — a reinstall issues a
 * new one, which correctly reads as off until it is registered.
 *
 * THE SAME SHAPE AS THE BROWSER'S, deliberately: `getSubscription()` answers
 * "has this browser got one", and the row on the server is what makes it
 * reachable. Neither side is authoritative alone, and `resyncNative` below
 * repairs the drift the same way `resync` does for web push.
 */
const NATIVE_KEY = 'cbm.push.registered';

function nativeRegistered(token) {
  if (!token) return false;
  try {
    return window.localStorage.getItem(NATIVE_KEY) === token;
  } catch (error) {
    // Private mode, or storage refused. The switch then reads off, the person
    // turns it on, and registering is idempotent — nothing breaks, it just asks.
    return false;
  }
}

function rememberNative(token) {
  try {
    if (token) window.localStorage.setItem(NATIVE_KEY, token);
    else window.localStorage.removeItem(NATIVE_KEY);
  } catch (error) { /* see above */ }
}

/**
 * Re-registers the app's token on load, where it says it is registered.
 *
 * The twin of `resync` for web push, and it exists for the same reason: the
 * server can lose a device — Firebase answers 404 for a token from an app that
 * was reinstalled, and we delete the row, correctly — while this side still
 * believes it is on. Re-posting is idempotent, costs one request, and is what
 * keeps the switch honest without anybody having to notice.
 */
async function resyncNative(native) {
  const token = typeof native.getToken === 'function' ? native.getToken() : '';
  if (!token || !nativeRegistered(token)) return;
  try {
    await api.pushDevice({ token, label: deviceLabel() });
  } catch (error) {
    // Nothing to say and nothing to do: the next load tries again.
  }
}

/** Whether this browser can do push at all. Safari on iOS needs installing first. */
function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Installs the worker on load, for everybody.
 *
 * IT USED TO WAIT FOR SOMEBODY TO WANT NOTIFICATIONS, which was right while the
 * worker did nothing else. It now also answers a failed navigation with
 * `/offline.html` — and that matters most inside the ANDROID APP, where a
 * network failure otherwise shows Chrome's own error page wearing our icon and
 * reads as "Cardboardmarket is broken".
 *
 * Somebody who never turns on notifications is exactly the person who most needs
 * that, so waiting for them to ask was waiting for the wrong thing.
 *
 * STILL NOT A CACHE. The worker caches one self-contained apology and nothing
 * else; every request the app makes goes to the network untouched. See sw.js.
 *
 * Failures are swallowed. A browser that refuses to register a worker — private
 * mode, an old iOS, a policy — should lose an offline page, not a marketplace.
 */
export function installWorker() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  register()
    .then(async (reg) => {
      /*
       * AND TELL THE SERVER WHAT THIS DEVICE IS HOLDING, on every load.
       *
       * The two sides drift and only one of them finds out. A push service
       * retires an endpoint; we delete the row, correctly, because it will never
       * work again — and the browser goes on handing back a subscription object,
       * so the switch still reads "on" for a device the server cannot reach.
       * Silently broken, and indistinguishable from working.
       *
       * The repair already existed and was reachable from one screen: it ran
       * when somebody opened their profile. Somebody whose notifications have
       * quietly stopped has no reason to go there — the symptom is that nothing
       * happens — so it now runs wherever the app is opened.
       *
       * Costs one request, and only for a device that HAS a subscription and
       * granted permission. Subscribing is idempotent: the endpoint is the key.
       */
      /*
       * ASKED OF THE SUBSCRIPTION, NEVER OF THE PERMISSION, and that was a bug
       * rather than a tidy-up. See `currentState` below: inside the Android app
       * `Notification.permission` reads "default" while a real subscription
       * exists, because the permission belongs to the Android app rather than
       * to the page — so gating on it skipped the repair on the one device
       * whose notifications had stopped.
       */
      if (!pushSupported()) return;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await resync(sub);
    })
    .catch(() => undefined);
}

/**
 * Registers the worker, once.
 *
 * Kept as a promise rather than a boolean so two callers racing — settings
 * mounting while something else asks — share one registration instead of
 * starting two.
 */
let registration = null;
function register() {
  if (!registration) {
    registration = navigator.serviceWorker.register('/sw.js', { scope: '/' })
      /*
       * Wait for it to be ACTIVE, not merely registered.
       *
       * `register()` resolves as soon as the worker is installing, and
       * `pushManager.subscribe()` against a registration with no active worker
       * fails — on the very first attempt, which is the only one that matters,
       * because every attempt after that finds the worker already running. It
       * looks exactly like a toggle that does nothing.
       */
      .then(() => navigator.serviceWorker.ready);
  }
  return registration;
}

/**
 * What this device's push state actually is, asked rather than remembered.
 *
 * Deliberately never cached in localStorage. Permission can be revoked from the
 * browser's own settings and a subscription can be dropped by the push service,
 * neither of which tells us — so a remembered "on" would be a switch showing the
 * wrong position, which is worse than no switch.
 */
export async function currentState() {
  /*
   * THE APP ANSWERS FOR ITSELF, and it answers first.
   *
   * Its notifications do not go through the browser at all, so every reading
   * below — the permission, the service worker, the subscription — is about a
   * mechanism this device is not using. Asking them would produce a switch that
   * described the wrong thing, which is the defect this whole area has been
   * corrected for twice.
   */
  const native = nativeApp();
  if (native) {
    const token = typeof native.getToken === 'function' ? native.getToken() : '';
    void resyncNative(native);
    return {
      supported: true,
      native: true,
      // `granted` describes ANDROID's permission, which this side cannot read —
      // what it can say is whether Firebase issued a token, and it only does
      // that for an app that is allowed to show notifications.
      permission: token ? 'granted' : 'default',
      // AND WHETHER WE ARE REGISTERED, not whether a token exists. See above:
      // reading the token made the switch spring back on when it was turned off.
      subscribed: nativeRegistered(token),
    };
  }
  if (!pushSupported()) return { supported: false, permission: 'unsupported', subscribed: false };
  const permission = window.Notification.permission;
  /*
   * THE SUBSCRIPTION IS THE TRUTH, AND THE PERMISSION IS NOT.
   *
   * This used to return "not subscribed" for anything but `granted`, which is
   * obvious and wrong on the one device that matters. REPORTED: "I enable push
   * notifications, it shows me everything ok and then I close the app... when I
   * open the app again and go to Settings I see that push notifications are
   * disabled. If I enable them again, I keep adding new targets to the server."
   *
   * Inside the ANDROID APP the permission belongs to the Android app rather than
   * to the page: notifications are delegated to it, and Chrome can report
   * `Notification.permission === "default"` while a perfectly good push
   * subscription exists and the OS is happy to display what it sends. His own
   * screenshot has the switch on, the server reaching the device, and this
   * reading "default" in the same breath.
   *
   * Under the old rule that read as OFF, every time: the switch fell back, the
   * repair below was skipped, and turning it "on" again minted another
   * subscription — which is precisely the growing list of targets he saw.
   *
   * So the switch asks the PushManager, which is the thing that actually decides
   * whether a push can arrive. The permission is still reported, because it is
   * what the screen needs to explain a REFUSAL, and `enable` still asks for it
   * before subscribing. It just no longer overrules a subscription that exists.
   */
  try {
    const reg = await register();
    const sub = await reg.pushManager.getSubscription();
    if (sub) await resync(sub);
    return { supported: true, permission, subscribed: Boolean(sub), endpoint: sub ? sub.endpoint : null };
  } catch (error) {
    return { supported: true, permission, subscribed: false };
  }
}

/**
 * Tells the server about the subscription this browser is holding, again.
 *
 * The two sides drift, and only one of them finds out. A push service can
 * retire an endpoint — we then delete the row, correctly, because it will never
 * work again — while the browser still hands back a subscription object. The
 * switch reads the BROWSER, so it goes on showing "on" for a device the server
 * has no way to reach: silently broken, and indistinguishable from working.
 *
 * `pushsubscriptionchange` is supposed to cover this and does not fire
 * reliably, so the repair does not depend on it. Subscribing is idempotent —
 * the endpoint is the key — so re-sending what we already have costs one
 * request and restores the row whenever it has gone missing.
 *
 * Once per page load. Failure is deliberately silent: this is repair, not an
 * action anybody asked for, and a notification about it would be noise.
 */
let resynced = false;
async function resync(sub) {
  if (resynced) return;
  resynced = true;
  const json = sub.toJSON();
  if (!json.keys || !json.keys.p256dh || !json.keys.auth) return;
  try {
    await api.pushSubscribe({
      endpoint: sub.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
      label: deviceLabel(),
    });
  } catch (error) {
    // Nothing to do and nothing to say: the switch still reflects the browser,
    // and the next load tries again.
  }
}

/**
 * Turns push on for this device.
 *
 * MUST be called from a click. Browsers only allow the permission prompt in
 * response to a gesture, and calling this on mount would either be ignored or —
 * worse, in browsers that still allow it — greet a first-time visitor with a
 * dialog they have no context for, which is how somebody chooses "never" and
 * cannot be asked again.
 */
export async function enable() {
  /*
   * IN THE APP, THERE IS NOTHING TO ASK FOR HERE.
   *
   * Android's own permission dialog is raised by the app when it starts, and the
   * token exists or it does not. What this does is hand that token to the
   * server, which is the same act as subscribing a browser: it is what turns a
   * device the app knows about into a device we can reach.
   */
  const native = nativeApp();
  if (native) {
    /*
     * ANDROID'S PERMISSION IS ASKED FOR HERE, BECAUSE HERE IS WHERE SOMEBODY
     * ASKED FOR IT.
     *
     * REPORTED: "we're asking for the notifications permission right away when
     * we open the app. That's not polite because the user doesn't know why we
     * need to send notifications."
     *
     * Right, and the screen around this switch is already the explanation a
     * well-behaved app shows first — three lines saying exactly what we would
     * send, and a switch labelled with what it does. Android's dialog then
     * arrives as the answer to something they just did, which is the only moment
     * a permission prompt is ever welcome.
     *
     * THE WAIT IS FOR AN EVENT, not a return value: the dialog is asynchronous
     * and the bridge is not, so the app fires `cbm-notification-permission` when
     * Android has been answered. The timeout is not a guess about how fast
     * somebody reads — it is there so a dialog that never appears, on a phone or
     * an Android version we have not seen, leaves a switch that failed rather
     * than a promise that never resolves.
     */
    if (typeof native.hasNotificationPermission === 'function'
        && !native.hasNotificationPermission()
        && typeof native.requestNotificationPermission === 'function') {
      await new Promise((resolve) => {
        let done = false;
        const finish = () => { if (done) return; done = true; window.removeEventListener('cbm-notification-permission', finish); resolve(); };
        window.addEventListener('cbm-notification-permission', finish);
        setTimeout(finish, 60000);
        native.requestNotificationPermission();
      });
      if (!native.hasNotificationPermission()) throw new Error('denied');
    }

    const token = typeof native.getToken === 'function' ? native.getToken() : '';
    if (!token) throw new Error('denied');
    await api.pushDevice({ token, label: deviceLabel() });
    rememberNative(token);
    return { endpoint: token };
  }
  if (!pushSupported()) throw new Error('unsupported');

  /*
   * PERMISSION FIRST, BEFORE ANY AWAIT.
   *
   * A browser only allows the prompt while the click's transient activation is
   * still live, and a network round-trip outlasts it. Asking for the key first
   * — which is what this used to do — meant the prompt was silently suppressed
   * and `enable()` rejected with nothing useful to say. From the person's side
   * that is a switch that does not move and no explanation, which is the worst
   * possible failure for a control whose whole job is to ask permission.
   */
  const permission = await window.Notification.requestPermission();
  if (permission !== 'granted') throw new Error(permission);

  const key = await api.pushKey();
  if (!key.available) throw new Error('unavailable');

  const reg = await register();
  // `userVisibleOnly` is not optional in Chrome: a subscription that promises
  // not to show anything is refused outright, because silent push is a tracking
  // channel rather than a notification one.
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: keyBytes(key.publicKey),
  });

  const json = sub.toJSON();
  await api.pushSubscribe({
    endpoint: sub.endpoint,
    p256dh: json.keys.p256dh,
    auth: json.keys.auth,
    label: deviceLabel(),
  });
  return sub;
}

/**
 * Turns it off for this device, on both sides.
 *
 * The browser subscription goes AND the row goes. Dropping only the row leaves a
 * device the push service still thinks is subscribed; dropping only the
 * subscription leaves us sending to an endpoint that will 410 forever. Neither
 * half is optional, and the row is removed first because a failure there is
 * recoverable while an unsubscribed browser cannot be re-subscribed silently.
 */
export async function disable() {
  /*
   * The row goes and the token stays. There is no "unsubscribe" to perform
   * against Firebase from here — the token belongs to the installation, not to
   * the account — so what "off" means for the app is that we hold no device to
   * send to, which is exactly what deleting the row achieves.
   */
  const native = nativeApp();
  if (native) {
    const token = typeof native.getToken === 'function' ? native.getToken() : '';
    if (token) await api.pushUnsubscribe(token).catch(() => undefined);
    // Forgotten here as well as on the server, or the switch reads the token
    // again on the next render and turns itself back on.
    rememberNative(null);
    return;
  }
  const reg = await register();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;
  await api.pushUnsubscribe(sub.endpoint).catch(() => undefined);
  await sub.unsubscribe();
}

/**
 * Something short enough to tell a phone from a laptop.
 *
 * Deliberately not the user agent. A full UA string is a fingerprint, and this
 * only exists so somebody looking at their own device list can recognise which
 * row is which.
 */
export function deviceLabel() {
  const ua = navigator.userAgent || '';
  const kind = /iPhone|iPad|Android/i.test(ua) ? 'Phone' : 'Computer';
  const browser = /Firefox/i.test(ua) ? 'Firefox'
    : /Edg/i.test(ua) ? 'Edge'
    : /Chrome/i.test(ua) ? 'Chrome'
    : /Safari/i.test(ua) ? 'Safari'
    : '';
  return browser ? `${kind} · ${browser}` : kind;
}

/**
 * SHOWS A NOTIFICATION FROM THIS PAGE, WITHOUT INVOLVING PUSH AT ALL.
 *
 * REPORTED: "I enable push notifications, it shows me everything ok and then I
 * close the app, send a message through another user and it never reaches. Then
 * when I open the app again I see that push notifications are disabled. If I
 * enable them again, I keep adding new targets to the server."
 *
 * The server side of that is already proved innocent: the outbox shows a push
 * ACCEPTED by Google's push service for that exact phone, one minute after it
 * subscribed. So the failure is somewhere between Google and the screen, and
 * three things live in that gap — the push transport, this worker's push
 * handler, and Android's own permission to display anything at all.
 *
 * This probe removes two of the three. It asks the worker to show a
 * notification directly, with no push and no server: if it appears, the device
 * can display our notifications and the problem is the transport; if it does
 * not, nothing we send will ever be seen, and the fix is in Android's settings
 * rather than in our code. On Android 13 an app may hold web-notification
 * permission in Chrome while the OS-level permission for the app is off, and
 * that combination fails exactly like this one.
 *
 * It is a diagnostic rather than a feature, and it is honest about that: it says
 * what it is doing, and nothing is stored.
 */
let asked = false;
export async function showLocalTestNotification(text) {
  /*
   * IN THE APP, THE APP POSTS IT.
   *
   * REPORTED, with the readings line beside it: "This device refused to show it
   * (unsupported)", `page=undefined`. An Android WebView does not implement the
   * Notification API at all — `window.Notification` is undefined, so every
   * browser path here is not merely unavailable, it does not exist.
   *
   * Which is the whole point of this app: the notification is the app's, so the
   * probe asks the app for one. Same channel, same icon, same tap handling as a
   * real push — a diagnostic that took a different path would prove nothing
   * about the real one.
   */
  const native = nativeApp();
  if (native) {
    if (typeof native.showTest !== 'function') throw new Error('unsupported');
    native.showTest(text);
    return;
  }
  if (!pushSupported()) throw new Error('unsupported');
  /*
   * NOT GATED ON THE PERMISSION EITHER, and this probe is how that was found.
   *
   * It refused with "default" on a phone whose switch was on, whose server row
   * was being reached, and whose Android settings showed notifications allowed.
   * The permission was reading the page's copy while the app held the real one.
   * Asking the worker to show a notification is the honest test: if the device
   * will display it, it appears, and if it will not, the browser rejects and the
   * screen says which — either answer is worth more than our guess about a
   * permission we cannot see properly.
   */
  /*
   * ASKS FIRST WHERE THE ORIGIN HAS NOT BEEN GRANTED, because this button is a
   * click and a click is the only moment a browser will allow the question.
   *
   * REPORTED, with the exact words from this probe: "Failed to execute
   * 'showNotification' on 'ServiceWorkerRegistration': No notification
   * permission has been granted for this origin" — on a phone whose switch was
   * on, whose subscription the server was reaching, and whose Android settings
   * allowed notifications.
   *
   * That state is contradictory and it is real: inside the Android app the
   * permission is delegated to the app, so a subscription can exist while the
   * ORIGIN's own permission was never granted or has since been reset. Refusing
   * here taught somebody the button was broken; asking is both the fix and the
   * measurement — if a prompt appears, the origin never had it.
   */
  if (window.Notification.permission !== 'granted') {
    /*
     * ASKED ONCE PER LOAD, NEVER ON EVERY PRESS.
     *
     * REPORTED: "even if I accept them, it asks me every time I click Show a
     * notification now". Inside the Android app the page's copy of the
     * permission can stay "default" however the person answers — the permission
     * belongs to the Android app — so a check that asks whenever it is not
     * granted asks forever. A control that reopens the same dialog after being
     * answered reads as broken, and teaches people to dismiss it.
     */
    if (asked) throw new Error(window.Notification.permission);
    asked = true;
    const answer = await window.Notification.requestPermission();
    if (answer !== 'granted') throw new Error(answer);
  }
  const reg = await register();
  await reg.showNotification("Cardboardmarket", {
    body: text,
    // PNG for the same reason the worker uses it: Android does not decode SVG
    // for a notification icon, and a probe that failed differently from the
    // real thing would prove nothing about the real thing.
    icon: '/icons/icon-192.png',
    badge: '/icons/icon-192-maskable.png',
    tag: 'cbm-test',
    data: { url: '/you' },
  });
}

/**
 * WHICH WORKER THIS DEVICE IS ACTUALLY RUNNING.
 *
 * A service worker outlives the page and updates on its own schedule, so the
 * code answering a push on somebody's phone is not necessarily the code we
 * shipped. An old worker with no push handler receives a push, shows nothing,
 * and Chrome eventually throws the subscription away — which is the reported
 * symptom, precisely.
 *
 * Asked of the worker rather than assumed, through a message channel, and null
 * where nothing answers: a worker too old to know the question is itself the
 * answer to it.
 */
export async function workerVersion() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;
  try {
    const reg = await register();
    const worker = reg.active;
    if (!worker) return null;
    return await new Promise((resolve) => {
      const channel = new MessageChannel();
      // A worker that does not answer must not hang the screen it is on.
      const timer = setTimeout(() => resolve(null), 1500);
      channel.port1.onmessage = (event) => {
        clearTimeout(timer);
        resolve((event.data && event.data.version) || null);
      };
      worker.postMessage({ ask: 'version' }, [channel.port2]);
    });
  } catch (error) {
    return null;
  }
}

/**
 * EVERYTHING THIS DEVICE WILL SAY ABOUT ITSELF, in one line.
 *
 * Built because two rounds of fixes were two rounds of inference from three
 * screenshots, and inference is the wrong tool when the device can simply be
 * asked. The contradictory state — a live subscription beside an origin with no
 * notification permission — is not one any of our code can produce, so what is
 * needed is the raw readings rather than another theory about them.
 *
 * `Notification.permission` is the page's copy. `navigator.permissions` is the
 * browser's own permission store, which is the one `showNotification` consults
 * and the one that said no. Where the two disagree, that disagreement IS the
 * answer.
 */
export async function pushDiagnosis() {
  /*
   * The app's readings are different readings. `page` and `browser` are about a
   * permission store the WebView does not have — printing "undefined" and a dash
   * there told the owner nothing except that something was wrong, which is the
   * opposite of what this line is for.
   */
  const native = nativeApp();
  if (native) {
    const token = typeof native.getToken === 'function' ? native.getToken() : '';
    return {
      supported: true,
      native: true,
      permission: token ? 'token issued' : 'no token',
      queried: nativeRegistered(token) ? 'registered' : 'not registered',
      // The tail only: a token is a capability, and eight characters are enough
      // to tell two installations apart.
      subscription: token ? token.slice(-8) : null,
    };
  }
  if (!pushSupported()) return { supported: false };
  const out = { supported: true, permission: window.Notification.permission, queried: null, subscription: null };
  try {
    if (navigator.permissions && navigator.permissions.query) {
      const status = await navigator.permissions.query({ name: 'notifications' });
      out.queried = status.state;
    }
  } catch (error) {
    out.queried = 'unreadable';
  }
  try {
    const reg = await register();
    const sub = await reg.pushManager.getSubscription();
    // The tail only. An endpoint is a capability — anything holding it can push
    // to this device — and eight characters are enough to tell two apart.
    out.subscription = sub ? sub.endpoint.slice(-8) : null;
  } catch (error) {
    out.subscription = 'unreadable';
  }
  return out;
}
